'use client';

import {
  PointStatus,
  useAddressStore,
} from '@/entities/address/model/use-address-store';
import { Brand } from '@/entities/brand/model/types';
import { useRouter } from '@/i18n/navigation';
import { useCustomLocale } from '@/shared/hooks/use-custom-locale';
import { useNavigationState } from '@/shared/store/use-navigation-state';
import { Locale } from '@/shared/types/locale';
import { AspectRatio, Badge, Flex, Heading, Image } from '@chakra-ui/react';
import { useMemo } from 'react';
import { hexToRgb } from '../lib/utils';

type Props = {
  brand: Brand;
};

const BrandCard: React.FC<Props> = ({ brand }) => {
  const locale = useCustomLocale() as Locale;
  const router = useRouter();

  const { points, openMap } = useAddressStore();
  const { setRedirectTo } = useNavigationState();

  const hasActiveAddress = useMemo(() => {
    return points.some((point) => point.status === PointStatus.ACTIVE);
  }, [points]);

  const goToRestaurant = () => {
    if (!hasActiveAddress) {
      setRedirectTo(`/restaurant/${brand.id}`);
      openMap();
    } else {
      router.push(`/restaurant/${brand.id}`);
    }
  };

  return (
    <Flex direction="column" onClick={goToRestaurant}>
      <AspectRatio ratio={2 / 1} w="full" rounded="xl" overflow="hidden">
        <Image
          src={process.env.BASE_URL + brand.logo}
          alt={brand.name}
          w="full"
          h="full"
          objectFit="cover"
        />
      </AspectRatio>

      <Flex direction="column" gap="1">
        <Heading as="h3" fontWeight="semibold" size="lg">
          {brand.name}
        </Heading>

        <Flex gap="2" flexWrap="wrap">
          {brand.tags.map((tag) => (
            <Badge
              key={tag.id}
              bg={`rgba(${hexToRgb(tag.color)}, 0.2)`}
              color={tag.color}
              rounded="xl"
              size="md"
            >
              {tag.icon && (
                <Image
                  src={process.env.BASE_URL + tag.icon}
                  alt=""
                  w="20px"
                  h="20px"
                  rounded="full"
                />
              )}
              {tag.title[locale]}
            </Badge>
          ))}
        </Flex>
      </Flex>
    </Flex>
  );
};

export default BrandCard;
